import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2, Building2, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, expired, accepted
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus('invalid');
      setError('No invitation token provided');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await apiHelpers.verifyInvite(token);
      setInviteData(response.data);
      setStatus('valid');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid invitation';
      if (detail.includes('expired')) {
        setStatus('expired');
      } else if (detail.includes('already been accepted')) {
        setStatus('accepted');
      } else {
        setStatus('invalid');
      }
      setError(detail);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiHelpers.acceptInvite(token, password);
      
      // Store the session token and set auth state
      localStorage.setItem('session_token', response.data.token);
      
      toast.success('Welcome!', { 
        description: 'Your account has been activated successfully.' 
      });
      
      // Redirect to dashboard
      // Use a small delay to ensure the auth context picks up the token
      setTimeout(() => {
        setSession(response.data.token, response.data.user, response.data.organization);
        navigate('/dashboard');
      }, 100);
      
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to accept invitation';
      setError(detail);
      toast.error('Failed to activate account', { description: detail });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Invitation</h1>
          <p className="text-slate-600 mb-6">
            {error || 'This invitation link is invalid or has already been used.'}
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Expired token
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invitation Expired</h1>
          <p className="text-slate-600 mb-6">
            This invitation has expired. Please contact your administrator to request a new invitation.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Already accepted
  if (status === 'accepted') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Already Activated</h1>
          <p className="text-slate-600 mb-6">
            This invitation has already been accepted. You can log in with your credentials.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Valid token - show password setup form
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Organization Header */}
        <div className="text-center mb-8">
          {inviteData?.organization?.logo_url ? (
            <img 
              src={inviteData.organization.logo_url} 
              alt={inviteData.organization.company_name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900">
            Welcome to {inviteData?.organization?.company_name || 'Our Team'}!
          </h1>
          <p className="text-slate-600 mt-2">
            Set up your account to get started
          </p>
        </div>

        {/* Setup Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* User Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{inviteData?.user?.name}</p>
                <p className="text-sm text-slate-600">{inviteData?.user?.email}</p>
                <p className="text-xs text-slate-500 capitalize">{inviteData?.user?.role}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-slate-700">Create Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pr-10"
                  data-testid="accept-invite-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="mt-1"
                data-testid="accept-invite-confirm-password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting}
              data-testid="accept-invite-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activating Account...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Activate My Account
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{' '}
          <button 
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:underline font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
